import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePropertyKitCategories, usePropertyKitItems, CONTROL_TYPE_LABELS, PropertyKitControlType } from '@/hooks/inventory';
import { uploadToCloudinary } from '@/lib/cloudinary';
import { Loader2, Upload, X } from 'lucide-react';

const formSchema = z.object({
  name: z.string().min(1, 'El nombre es requerido'),
  category_id: z.string().min(1, 'La categoría es requerida'),
  control_type: z.enum(['fixed_asset', 'consumable', 'countable']),
  ideal_quantity: z.number().nullable().optional(),
  description: z.string().nullable().optional(),
  image_url: z.string().nullable().optional(),
});

type FormData = z.infer<typeof formSchema>;

interface ItemFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingItemId?: string | null;
}

export default function ItemFormModal({ open, onOpenChange, editingItemId }: ItemFormModalProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const { categories } = usePropertyKitCategories();
  const { createItem, updateItem, getItem, isCreating, isUpdating } = usePropertyKitItems();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      category_id: '',
      control_type: 'fixed_asset',
      ideal_quantity: null,
      description: null,
      image_url: null,
    },
  });

  const controlType = form.watch('control_type');

  // Load item data when editing - only run when editingItemId or open changes
  useEffect(() => {
    const loadItem = async () => {
      if (editingItemId && open) {
        const item = await getItem(editingItemId);
        if (item) {
          form.reset({
            name: item.name,
            category_id: item.category_id,
            control_type: item.control_type,
            ideal_quantity: item.ideal_quantity,
            description: item.description,
            image_url: item.image_url,
          });
          setPreviewUrl(item.image_url);
        }
      } else if (!editingItemId && open) {
        form.reset({
          name: '',
          category_id: '',
          control_type: 'fixed_asset',
          ideal_quantity: null,
          description: null,
          image_url: null,
        });
        setPreviewUrl(null);
      }
    };

    loadItem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingItemId, open]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const url = await uploadToCloudinary(file);
      form.setValue('image_url', url);
      setPreviewUrl(url);
    } catch (error) {
      console.error('Error uploading image:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveImage = () => {
    form.setValue('image_url', null);
    setPreviewUrl(null);
  };

  const onSubmit = async (data: FormData) => {
    try {
      const itemData = {
        name: data.name,
        category_id: data.category_id,
        control_type: data.control_type,
        ideal_quantity: data.ideal_quantity ?? null,
        description: data.description ?? null,
        image_url: data.image_url ?? null,
      };
      
      if (editingItemId) {
        await updateItem({ id: editingItemId, ...itemData });
      } else {
        await createItem(itemData);
      }
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving item:', error);
    }
  };

  const isLoading = isCreating || isUpdating;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {editingItemId ? 'Editar artículo' : 'Nuevo artículo'}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nombre</FormLabel>
                  <FormControl>
                    <Input placeholder="Ej: Cafetera Nespresso" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="category_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Categoría</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona una categoría" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="control_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo de control</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.entries(CONTROL_TYPE_LABELS).map(([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {controlType === 'countable' && (
              <FormField
                control={form.control}
                name="ideal_quantity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cantidad ideal (sugerida)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={0}
                        placeholder="Ej: 4"
                        {...field}
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                      />
                    </FormControl>
                    <p className="text-xs text-muted-foreground">
                      Este valor se usará como referencia al agregar el artículo a propiedades
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descripción (opcional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Descripción del artículo..."
                      {...field}
                      value={field.value ?? ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Image upload */}
            <div className="space-y-2">
              <FormLabel>Imagen (opcional)</FormLabel>
              {previewUrl ? (
                <div className="relative inline-block">
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="h-24 w-24 rounded-lg object-cover"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute -top-2 -right-2 h-6 w-6"
                    onClick={handleRemoveImage}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <label className="cursor-pointer">
                    <div className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50">
                      {isUploading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Upload className="h-4 w-4" />
                      )}
                      <span className="text-sm">Subir imagen</span>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageUpload}
                      disabled={isUploading}
                    />
                  </label>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isLoading}
                className="bg-brand-primary hover:bg-brand-primary-hover"
              >
                {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingItemId ? 'Guardar cambios' : 'Crear artículo'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
